export type SurveyJSONValue =
  | string
  | number
  | boolean
  | null
  | SurveyJSONValue[]
  | { [key: string]: SurveyJSONValue }

export interface SurveyContent {
  steps: Record<string, SurveyStep>
}

export const SurveyStepTypes = {
  SELECT_ICON: 'select-icon',
  SELECT_BUTTON: 'select-button',
  MESSAGE_ICON: 'message-icon',
  FORM: 'form'
} as const

export type SurveyStep =
  | SurveySelectIconStep
  | SurveySelectButtonStep
  | SurveyMessageIconStep
  | SurveyFormStep

interface SurveyStepBase {
  title?: string
  subtitle?: string
  'close-actions'?: SurveyAction[] | null
}

export interface SurveySelectIconStep extends SurveyStepBase {
  type: 'select-icon'
  choices: SurveySelectIconChoice[]
}

export interface SurveySelectButtonStep extends SurveyStepBase {
  type: 'select-button'
  choices: SurveySelectBtnChoice[]
}

export interface SurveyMessageIconStep extends SurveyStepBase {
  type: 'message-icon'
  icon?: string
  message?: string
  'info-message'?: string
  buttons?: SurveyButton[]
}

export interface SurveyFormStep extends SurveyStepBase {
  type: 'form'
  questions: Questions[]
}

/* =========================
   Choices
========================= */

export interface SurveySelectIconChoice {
  name: string
  icon: string
  actions?: SurveyAction[]
}

export interface SurveySelectBtnChoice {
  name: string
  icon?: string
  actions?: SurveyAction[]
}

/* =========================
   Questions
========================= */

export const SurveyFieldTypes = {
  MULTI_CHOICE: 'multi-choice',
  FREE_TEXT: 'free-text',
  BUTTONS: 'buttons'
} as const

export type Questions =
  | SurveyMultiChoiceQuestion
  | SurveyFreeTextQuestion
  | SurveyButtonsQuestion

interface SurveyQuestionBase {
  type: string
  title?: string
}

export interface SurveyMultiChoiceQuestion extends SurveyQuestionBase {
  type: 'multi-choice'
  'set-field'?: string
  choices: SurveyMultiChoiceOption[]
}

export interface SurveyFreeTextQuestion extends SurveyQuestionBase {
  type: 'free-text'
  'max-len'?: number
  'set-field'?: string
}

export interface SurveyMultiChoiceOption {
  name: string
  value?: string | number
}

export interface SurveyButtonsQuestion extends SurveyQuestionBase {
  type: 'buttons'
  buttons: SurveyButton[]
}

/* =========================
   Buttons
========================= */

export interface SurveyButton {
  title: string
  style?: 'primary' | 'secondary'
  'enable-if'?: SurveyCondition[]
  actions?: SurveyAction[]
}

/* =========================
   Actions
========================= */

export const SurveyActionTypes = {
  SUBMIT: 'submit-survey',
  FIELD_SET: 'field-set',
  GO_TO: 'goto',
  HISTORY_BACK: 'history-back',
  EXIT: 'exit'
} as const

export type SurveyAction =
  | SurveyFieldSetAction
  | SurveySubmitSurveyAction
  | SurveyGotoAction
  | SurveyHistoryBackAction
  | SurveyExitAction

interface SurveyActionBase {
  action: string
  if?: SurveyCondition[]
}

export interface SurveySubmitSurveyAction extends SurveyActionBase {
  action: 'submit-survey'
}

export interface SurveyFieldSetAction extends SurveyActionBase {
  action: 'field-set'
  field: string
  value: SurveyJSONValue
}

export interface SurveyGotoAction extends SurveyActionBase {
  action: 'goto'
  to: string
  'push-history'?: boolean
}

export interface SurveyHistoryBackAction extends SurveyActionBase {
  action: 'history-back'
}

export interface SurveyExitAction extends SurveyActionBase {
  action: 'exit'
}

/* =========================
   Conditions
========================= */

export const SurveyConditionsTypes = {
  FIELD_EMPTY: 'field-empty',
  FIELD_CONTAINS: 'field-contains',
  ANY: 'any',
  ALL: 'all'
} as const

export type SurveyCondition =
  | SurveyFieldEmptyCondition
  | SurveyFieldContainsCondition
  | SurveyAnyCondition
  | SurveyAllCondition

interface SurveyConditionBase {
  op: string
  not?: boolean
}

export interface SurveyFieldEmptyCondition extends SurveyConditionBase {
  op: 'field-empty'
  field: string
}

export interface SurveyFieldContainsCondition extends SurveyConditionBase {
  op: 'field-contains'
  field: string
  value: SurveyJSONValue
}

export interface SurveyAnyCondition extends SurveyConditionBase {
  op: 'any'
  conds: SurveyCondition[]
}

export interface SurveyAllCondition extends SurveyConditionBase {
  op: 'all'
  conds: SurveyCondition[]
}